import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { User, LogOut, Sparkles, ShoppingBag, Download } from "lucide-react";

const ROLE_LABEL = { admin: "Admin", sales: "Vânzări", client: "Client" };

const NAV = [
  { to: "/studio", label: "Studio AI", roles: ["admin", "sales", "client"] },
  { to: "/projects", label: "Proiecte", roles: ["admin", "sales", "client"] },
  { to: "/catalog", label: "Catalog", roles: ["admin", "sales", "client"] },
  { to: "/sales", label: "Cereri ofertă", roles: ["admin", "sales"] },
  { to: "/admin", label: "Administrare", roles: ["admin"] },
];

export function Header() {
  const { user, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const loc = useLocation();

  const doLogout = async () => { await logout(); navigate("/login"); };
  const links = user ? NAV.filter((n) => n.roles.includes(user.role)) : [];

  return (
    <header className="sticky top-0 z-50 glass border-b border-slate-800 h-16 flex items-center">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
        <Link to={user ? "/studio" : "/"} data-testid="header-home-link">
          <Logo className="h-7 sm:h-8" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                loc.pathname === n.to ? "text-white bg-slate-800" : "text-slate-400 hover:text-white"
              }`}
            >
              {t(n.label)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LangToggle className="hidden sm:inline-flex" />
          <a
            href="https://klusled.ro/magazin/"
            target="_blank"
            rel="noreferrer"
            data-testid="header-shop-link"
            className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 hover:text-accent transition-colors"
          >
            <ShoppingBag className="w-4 h-4" /> {t("Magazin KLUS")}
          </a>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="profile-menu-trigger" className="gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </span>
                  <span className="hidden sm:inline text-sm text-slate-200 font-medium max-w-[140px] truncate" data-testid="header-user-name">
                    {(user.full_name || "").trim().split(/\s+/)[0] || user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover border-slate-800">
                <DropdownMenuLabel className="truncate">{user.full_name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/studio")} data-testid="menu-studio">
                  <Sparkles className="w-4 h-4 mr-2" /> {t("Studio AI")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/projects")} data-testid="menu-projects">
                  {t("Proiectele mele")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/instalare")} data-testid="menu-install">
                  <Download className="w-4 h-4 mr-2" /> {t("Instalează aplicația")}
                </DropdownMenuItem>
                <DropdownMenuItem className="sm:hidden focus:bg-transparent" data-testid="menu-lang">
                  <LangToggle />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-primary">
                  <LogOut className="w-4 h-4 mr-2" /> {t("Deconectare")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <LangToggle className="sm:hidden" />
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login-btn">
                {t("Autentificare")}
              </Button>
              <Button size="sm" onClick={() => navigate("/register")} data-testid="header-register-btn"
                className="bg-primary hover:bg-primary/90 text-white">
                {t("Creează cont")}
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
